import { cn } from '../../../lib/utils';

export type CharlieSpinnerProps = React.SVGAttributes<SVGSVGElement>;

/**
 * CharlieSpinner — A rotating ring for loading and waiting states.
 *
 * Drawn in `currentColor`, so set the colour with a text class. 32px by
 * default; set the size with a `size-*` class.
 *
 * Unlike Rocket's conic `RocketSpinner`, this is the plain ring the Ronan
 * designs use.
 *
 * ## Usage
 *
 * ```tsx
 * import { CharlieSpinner } from '@ver.id/libs.ui.charlie';
 *
 * // Next to a visible status text
 * <CharlieSpinner className="text-muted-foreground" />
 *
 * // Inline in a button (CharlieButton's `loading` does this for you)
 * <CharlieSpinner className="size-4" />
 *
 * // On its own: give it an accessible name
 * <CharlieSpinner role="status" aria-hidden={false} aria-label="Loading" />
 * ```
 *
 * ## Gotchas
 *
 * - **Hidden from screen readers by default**, because it usually sits next
 *   to text that already says what is happening. When it stands alone, pass
 *   `role="status"`, `aria-hidden={false}` and an `aria-label`.
 */
export function CharlieSpinner({ className, ...props }: CharlieSpinnerProps) {
  return (
    <svg
      data-slot="spinner"
      className={cn('size-8 animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
